import { Injectable } from '@angular/core';
import {
  DATE_RANGE_OPTIONS,
  DEFAULT_DATE_RANGE_OPTION,
  DateRangeOption,
} from '../models/billing-history.models';

/**
 * Enum for page keys used in user cookie settings
 */
export enum PageKey {
  Dashboard = 'dashboard',
  ResourceDetails = 'resourceDetails',
  MatterDetails = 'matterDetails',
  ResourcesDirectory = 'resourcesDirectory',
  TimeOffRequests = 'timeOffRequests',
  Requests = 'requests',
  ClientsMatters = 'clientsMatters',
}

/**
 * Service for managing low importance user preferences stored in cookies
 * For high importance settings, use a backend service instead
 */
@Injectable({
  providedIn: 'root',
})
export class UserCookieService {
  private readonly COOKIE_NAME = 'stb_user_settings';
  private readonly COOKIE_EXPIRY_DAYS = 365; // 1 year

  // Browsers cap a single cookie at roughly 4 KB and silently ignore a
  // `document.cookie` assignment that exceeds it — every page's preferences
  // would then be stranded at their last good value with no error anywhere.
  // Every preference in the app shares this one cookie, so warn when a write
  // gets close instead of failing invisibly.
  private readonly MAX_COOKIE_BYTES = 4096;
  // Rough allowance for `name=` plus the expires/path/SameSite attributes.
  private readonly COOKIE_OVERHEAD_BYTES = 128;

  constructor() {}

  /**
   * Get billing history timespan setting for a specific page
   * @param pageKey The page identifier
   * @returns The saved DateRangeOption or the default (30 days)
   */
  getBillingHistoryTimespan(pageKey: PageKey): DateRangeOption {
    const settings = this.getSettings();
    const saved = settings.billingHistoryTimespan?.[pageKey];

    if (saved !== undefined) {
      // Current format stores the stable option key (string).
      // Legacy cookies stored the numeric `days` value — fall back to matching that.
      const option =
        typeof saved === 'number'
          ? DATE_RANGE_OPTIONS.find((opt) => opt.days === saved)
          : DATE_RANGE_OPTIONS.find((opt) => opt.key === saved);
      if (option) {
        return option;
      }
    }

    // Return default (Last 30 Days)
    return DEFAULT_DATE_RANGE_OPTION;
  }

  /**
   * Set billing history timespan setting for a specific page
   * @param pageKey The page identifier
   * @param option The DateRangeOption to save
   */
  setBillingHistoryTimespan(pageKey: PageKey, option: DateRangeOption): void {
    const settings = this.getSettings();

    if (!settings.billingHistoryTimespan) {
      settings.billingHistoryTimespan = {};
    }

    settings.billingHistoryTimespan[pageKey] = option.key;
    this.saveSettings(settings);
  }

  /**
   * Get custom date range for a specific page
   * @param pageKey The page identifier
   * @returns The saved custom date range or null
   */
  getCustomDateRange(
    pageKey: PageKey,
  ): { startDate: Date; endDate: Date } | null {
    const settings = this.getSettings();
    const savedRange = settings.customDateRanges?.[pageKey];

    if (savedRange?.startDate && savedRange?.endDate) {
      return {
        startDate: new Date(savedRange.startDate),
        endDate: new Date(savedRange.endDate),
      };
    }

    return null;
  }

  /**
   * Set custom date range for a specific page
   * @param pageKey The page identifier
   * @param startDate The start date
   * @param endDate The end date
   */
  setCustomDateRange(pageKey: PageKey, startDate: Date, endDate: Date): void {
    const settings = this.getSettings();

    if (!settings.customDateRanges) {
      settings.customDateRanges = {};
    }

    settings.customDateRanges[pageKey] = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };

    this.saveSettings(settings);
  }

  /**
   * Get all settings from cookie
   */
  private getSettings(): UserSettings {
    const cookieValue = this.getCookie(this.COOKIE_NAME);

    if (cookieValue) {
      try {
        return JSON.parse(decodeURIComponent(cookieValue));
      } catch (e) {
        console.warn('Failed to parse user settings cookie:', e);
        return {};
      }
    }

    return {};
  }

  /**
   * Save settings to cookie
   */
  private saveSettings(settings: UserSettings): void {
    const jsonString = JSON.stringify(settings);
    const encodedValue = encodeURIComponent(jsonString);

    // Surface an over-limit write; the browser would otherwise drop it without
    // a trace and every page's preferences would quietly stop saving.
    if (
      encodedValue.length + this.COOKIE_OVERHEAD_BYTES >
      this.MAX_COOKIE_BYTES
    ) {
      console.warn(
        `User settings cookie is ${encodedValue.length} bytes, which exceeds the ` +
          `~${this.MAX_COOKIE_BYTES}-byte browser limit. Preferences may stop persisting.`,
      );
    }

    this.setCookie(this.COOKIE_NAME, encodedValue, this.COOKIE_EXPIRY_DAYS);
  }

  /**
   * Get a cookie value by name
   */
  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      let c = cookie.trim();
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length);
      }
    }

    return null;
  }

  /**
   * Set a cookie with name, value, and expiry days
   */
  private setCookie(name: string, value: string, days: number): void {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + date.toUTCString();

    // Set cookie with path to make it available across the entire app
    document.cookie = `${name}=${value}; ${expires}; path=/; SameSite=Strict`;
  }

  /**
   * Get a saved filter selection for a specific page and filter
   * @param pageKey The page identifier
   * @param filterName The filter identifier
   * @returns The saved string array or null if not set
   */
  getFilterSelection(pageKey: PageKey, filterName: string): string[] | null {
    const settings = this.getSettings();
    const saved = settings.filterSelections?.[pageKey]?.[filterName];
    return saved ?? null;
  }

  /**
   * Set a filter selection for a specific page and filter
   * @param pageKey The page identifier
   * @param filterName The filter identifier
   * @param values The selected values to persist
   */
  setFilterSelection(
    pageKey: PageKey,
    filterName: string,
    values: string[],
  ): void {
    const settings = this.getSettings();

    if (!settings.filterSelections) {
      settings.filterSelections = {};
    }
    if (!settings.filterSelections[pageKey]) {
      settings.filterSelections[pageKey] = {};
    }

    settings.filterSelections[pageKey][filterName] = values;
    this.saveSettings(settings);
  }

  /**
   * Remove a saved filter selection entirely, reclaiming its space in the
   * shared settings cookie. Used to drop keys a page no longer persists.
   * @param pageKey The page identifier
   * @param filterName The filter identifier
   */
  removeFilterSelection(pageKey: PageKey, filterName: string): void {
    const settings = this.getSettings();
    this.removeFromBucket(
      settings,
      settings.filterSelections?.[pageKey],
      filterName,
    );
  }

  /**
   * Get a saved single-value filter (string) for a specific page and filter
   * @param pageKey The page identifier
   * @param filterName The filter identifier
   * @returns The saved string value or null if not set
   */
  getFilterValue(pageKey: PageKey, filterName: string): string | null {
    const settings = this.getSettings();
    const saved = settings.filterValues?.[pageKey]?.[filterName];
    return saved ?? null;
  }

  /**
   * Set a single-value filter (string) for a specific page and filter
   * @param pageKey The page identifier
   * @param filterName The filter identifier
   * @param value The value to persist (pass empty string to clear)
   */
  setFilterValue(pageKey: PageKey, filterName: string, value: string): void {
    const settings = this.getSettings();

    if (!settings.filterValues) {
      settings.filterValues = {};
    }
    if (!settings.filterValues[pageKey]) {
      settings.filterValues[pageKey] = {};
    }

    settings.filterValues[pageKey][filterName] = value;
    this.saveSettings(settings);
  }

  /**
   * Remove a saved single-value filter entirely, reclaiming its space in the
   * shared settings cookie. Used to drop keys a page no longer persists.
   *
   * The single-value counterpart to removeFilterSelection: that one only touches
   * `filterSelections`, so it silently no-ops on anything written by
   * setFilterValue, which writes to `filterValues`.
   * @param pageKey The page identifier
   * @param filterName The filter identifier
   */
  removeFilterValue(pageKey: PageKey, filterName: string): void {
    const settings = this.getSettings();
    this.removeFromBucket(
      settings,
      settings.filterValues?.[pageKey],
      filterName,
    );
  }

  /**
   * Drops one filter from a page's bucket and re-saves, or does nothing if the bucket or the key
   * is absent — so an unnecessary cookie write never happens.
   *
   * `filterSelections` and `filterValues` are separate buckets holding different value types, and
   * a remove against one must never touch the other. That is why removeFilterSelection could not
   * simply be reused for single values; sharing the body instead of copying it keeps the two
   * removals from drifting apart while the caller still picks the bucket explicitly.
   */
  private removeFromBucket(
    settings: UserSettings,
    bucket: Record<string, unknown> | undefined,
    filterName: string,
  ): void {
    if (!bucket || !(filterName in bucket)) {
      return;
    }

    delete bucket[filterName];
    this.saveSettings(settings);
  }

  /**
   * Clear all user settings
   */
  clearSettings(): void {
    this.setCookie(this.COOKIE_NAME, '', -1);
  }
}

/**
 * Interface for low importance user settings stored in cookie
 */
interface UserSettings {
  billingHistoryTimespan?: {
    // Maps page key to the option key (string). Legacy cookies may hold the
    // numeric `days` value, which is still resolved for backward compatibility.
    [pageKey: string]: string | number;
  };
  customDateRanges?: {
    [pageKey: string]: {
      startDate: string;
      endDate: string;
    };
  };
  filterSelections?: {
    [pageKey: string]: {
      [filterName: string]: string[];
    };
  };
  filterValues?: {
    [pageKey: string]: {
      [filterName: string]: string;
    };
  };
}
